import type {
  AnalyticsBrowser,
  InAppPluginSettings,
  InitOptions,
} from '@customerio/cdp-analytics-browser'
import { CUSTOMERIO } from '@/config/constants'

/**
 * Customer.io web in-app messages. edge-user-api identifies the profile on
 * edgeUserId and owns every trait, so the browser identifies with that id alone.
 * Page events exist only so in-app page rules know the route (PostHog captures
 * pageviews), and go out only while a user is identified.
 *
 * Every call is a no-op without a write key, and the SDK chunk is fetched on the
 * first identify, so a build without the key never downloads it.
 */

// AnalyticsBrowser is thenable: keep it here and never await it or return it from
// a promise callback, or it unwraps and the buffered identify/page/reset are lost.
let client: AnalyticsBrowser | null = null
let loading: Promise<void> | null = null

// What the app wants (identifiedId, currentPath) against what the SDK was sent
// (sentId, sentPath). flush() reconciles them, so a logout that lands while the
// chunk loads sends nothing, and repeats of the same id or path are dropped.
let identifiedId: string | null = null
let currentPath: string | null = null
let sentId: string | null = null
let sentPath: string | null = null

// Replaced by the router's navigate at startup, so in-app links move without a reload.
let navigate: (path: string) => void = (path) => window.location.assign(path)

const IN_APP_PLUGIN = 'Customer.io In-App Plugin'
const OPEN_URL_ACTION = 'gist://loadPage'

interface InAppActionDetail {
  actionValue?: string
  message?: { dismiss?: () => void }
}

/**
 * The in-app plugin's events listener. A button that links inside the app sends the
 * path as a Perform action ("/account/demo"), which the SDK leaves to us: dismiss
 * first, so Customer.io records it and the message does not show again, then route
 * in the app. An Open URL button reloads the page right after this returns, before a
 * dismiss can land, so internal links should use the path action; for Open URL this
 * dismiss is best effort. External links and named actions keep the SDK's behavior.
 */
export function handleInAppEvent(event: Event) {
  if (event.type !== 'in-app:message-action') return
  const { actionValue = '', message } = (event as CustomEvent<InAppActionDetail>).detail ?? {}
  const isPath = actionValue.startsWith('/') && !actionValue.startsWith('//')
  if (!isPath && !actionValue.startsWith(OPEN_URL_ACTION)) return
  message?.dismiss?.()
  if (isPath) navigate(actionValue)
}

/** The app router's navigate, used for in-app message links. */
export function setCustomerIoNavigate(fn: (path: string) => void) {
  navigate = fn
}

function ensureClient(writeKey: string): Promise<void> {
  loading ??= import('@customerio/cdp-analytics-browser').then(
    ({ AnalyticsBrowser }) => {
      // Merged over the workspace's plugin settings, so its siteId still applies.
      // InitOptions types integrations as JSON; the plugin's own settings type takes
      // the events listener, which the SDK hands to the plugin unchanged.
      const inApp: InAppPluginSettings = { events: handleInAppEvent }
      const options = { integrations: { [IN_APP_PLUGIN]: inApp } } as unknown as InitOptions
      client = AnalyticsBrowser.load({ writeKey }, options)
    },
    // A failed chunk fetch leaves in-app off for now; the next identify retries.
    () => {
      loading = null
    },
  )
  return loading
}

function flush() {
  if (!client || !identifiedId) return
  if (sentId !== identifiedId) {
    void client.identify(identifiedId)
    sentId = identifiedId
    sentPath = null
  }
  if (currentPath && currentPath !== sentPath) {
    // Bare page(): the SDK takes the url from window.location, which page rules match.
    void client.page()
    sentPath = currentPath
  }
}

/** `id` must be edgeUserId, the backend's profile id; any other id duplicates the profile. */
export function identifyCustomerIo(id: string | undefined) {
  const { writeKey } = CUSTOMERIO
  if (!writeKey || !id || id === identifiedId) return
  identifiedId = id
  void ensureClient(writeKey).then(flush)
}

export function resetCustomerIo() {
  identifiedId = null
  if (client && sentId) void client.reset()
  sentId = null
  sentPath = null
}

/** The route as pathname + search; the router feeds every change. */
export function setCustomerIoPath(path: string) {
  currentPath = path
  flush()
}
